import React, { createContext, useContext, useEffect, useState, useRef, useCallback } from "react";
import axios from "axios";
import { Link, useLocation, useNavigate, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { LogIn, UserPlus, Loader2, Mail, Lock, User as UserIcon } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const http = axios.create({ baseURL: API, withCredentials: true });

// ---------------------------------------------------------------------------
// Utilities
// ---------------------------------------------------------------------------
function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

// ---------------------------------------------------------------------------
// AuthContext
// ---------------------------------------------------------------------------
const AuthCtx = createContext(null);
export const useAuth = () => useContext(AuthCtx);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);      // null while checking
  const [ready, setReady] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const { data } = await http.get("/auth/me");
      setUser(data);
      return data;
    } catch (e) {
      setUser(false);
      return null;
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setReady(true);
      return;
    }
    (async () => { await refresh(); setReady(true); })();
  }, [refresh]);

  const login = async (email, password) => {
    const { data } = await http.post("/auth/login", { email, password });
    setUser(data);
    return data;
  };
  const register = async (payload) => {
    const { data } = await http.post("/auth/register", payload);
    setUser(data);
    return data;
  };
  const logout = async () => {
    try { await http.post("/auth/logout"); } catch { /* noop */ }
    setUser(false);
  };
  const forgotPassword = async (email) => (await http.post("/auth/forgot-password", { email })).data;
  const resetPassword = async (token, password) => (await http.post("/auth/reset-password", { token, password })).data;
  const confirmEmail = async (token) => {
    const r = (await http.post("/auth/verify-email/confirm", { token })).data;
    await refresh();
    return r;
  };

  const value = { user, ready, http, login, register, logout, refresh, forgotPassword, resetPassword, confirmEmail };
  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export function ProtectedRoute({ children }) {
  const { user, ready } = useAuth();
  const location = useLocation();
  if (!ready || user === null) {
    return <div className="min-h-screen flex items-center justify-center bg-ivory"><Loader2 className="animate-spin text-gold" size={28} /></div>;
  }
  if (!user) return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  return children;
}

// ---------------------------------------------------------------------------
// Auth Callback (Emergent Google redirect target)
// ---------------------------------------------------------------------------
export function AuthCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const { refresh } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    const hash = window.location.hash || "";
    const m = /session_id=([^&]+)/.exec(hash);
    if (!m) { navigate("/login", { replace: true }); return; }
    (async () => {
      try {
        await http.post("/auth/google/session", { session_id: decodeURIComponent(m[1]) });
        await refresh();
        // Clean the URL
        window.history.replaceState({}, document.title, "/portal");
        navigate("/portal", { replace: true });
        toast.success("Signed in with Google");
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Google sign-in failed");
        navigate("/login", { replace: true });
      }
    })();
  }, [navigate, refresh]);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-ivory gap-3">
      <Loader2 className="animate-spin text-gold" size={28} />
      <p className="overline text-ink/60">Signing you in…</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared Layout
// ---------------------------------------------------------------------------
function AuthShell({ eyebrow, title, subtitle, children, right }) {
  return (
    <div className="min-h-screen bg-ivory pt-28 pb-20 px-6 md:px-12">
      <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-16 items-start">
        <div>
          <motion.p initial={{opacity:0,y:12}} animate={{opacity:1,y:0}} transition={{duration:.5}} className="eyebrow text-gold">{eyebrow}</motion.p>
          <motion.h1 initial={{opacity:0,y:16}} animate={{opacity:1,y:0}} transition={{duration:.6,delay:.05}} className="font-serif text-4xl md:text-5xl mt-3 text-ink leading-tight">{title}</motion.h1>
          {subtitle && <p className="mt-5 text-ink/70 max-w-md">{subtitle}</p>}
          <div className="hidden md:block mt-10 relative aspect-[4/5] overflow-hidden">
            <img src="https://images.unsplash.com/photo-1600585154340-be6161a56a0c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400" className="w-full h-full object-cover" alt="Luxury interior" />
            <div className="absolute inset-0 bg-gradient-to-t from-ink/60 via-transparent to-transparent" />
            <div className="absolute bottom-6 left-6 right-6 text-white">
              <p className="eyebrow">Your Design Portal</p>
              <p className="font-serif text-2xl mt-1">Every color, every home, one place.</p>
            </div>
          </div>
        </div>
        <div className="bg-white border border-black/10 p-8 md:p-10 shadow-sm">{children}</div>
      </div>
    </div>
  );
}

function GoogleButton({ label = "Continue with Google" }) {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const onClick = () => {
    const redirectUrl = window.location.origin + "/portal";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid="google-signin-btn"
      className="w-full border border-black/15 py-3 px-4 flex items-center justify-center gap-3 hover:bg-black/[0.03] transition-colors"
    >
      <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.4 29.3 35.5 24 35.5c-6.4 0-11.5-5.1-11.5-11.5S17.6 12.5 24 12.5c2.9 0 5.6 1.1 7.6 2.9l5.7-5.7C33.7 6.1 29.1 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.4-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.6 15.3 18.9 12.5 24 12.5c2.9 0 5.6 1.1 7.6 2.9l5.7-5.7C33.7 6.1 29.1 4 24 4 16.3 4 9.7 8.4 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.1 0 9.7-1.9 13.2-5.1l-6.1-5c-2 1.4-4.5 2.2-7.1 2.2-5.3 0-9.7-3.1-11.3-7.5l-6.5 5C9.6 39.5 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.4-2.4 4.4-4.4 5.9l6.1 5c-.4.4 6.6-4.8 6.6-14.9 0-1.2-.1-2.4-.4-3.5z"/></svg>
      <span className="text-sm">{label}</span>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Login / Signup / Forgot / Reset / Verify
// ---------------------------------------------------------------------------
export function Login() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => { if (user) navigate(location.state?.from || "/portal", { replace: true }); }, [user, navigate, location.state]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      navigate(location.state?.from || "/portal", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <AuthShell eyebrow="Client Portal" title="Sign in to your design portal" subtitle="Access your saved colors, quiz results, homes and estimates.">
      <form onSubmit={onSubmit} className="space-y-5" data-testid="login-form">
        <GoogleButton label="Sign in with Google" />
        <Divider label="or with email" />
        <Field icon={<Mail size={16}/>} label="Email">
          <input required type="email" value={email} onChange={(e)=>setEmail(e.target.value)} className="input-plain" data-testid="login-email" />
        </Field>
        <Field icon={<Lock size={16}/>} label="Password" trailing={<Link to="/forgot-password" className="text-xs text-gold hover:underline" data-testid="link-forgot">Forgot?</Link>}>
          <input required type="password" value={password} onChange={(e)=>setPassword(e.target.value)} className="input-plain" data-testid="login-password" />
        </Field>
        {error && <p className="text-sm text-red-600" data-testid="login-error">{error}</p>}
        <Button type="submit" disabled={loading} data-testid="login-submit" className="w-full bg-ink text-white rounded-none hover:bg-gold py-6 overline">
          {loading ? "Signing in…" : (<span className="flex items-center justify-center gap-2"><LogIn size={16}/> Sign in</span>)}
        </Button>
        <p className="text-sm text-ink/70 text-center">New here? <Link to="/signup" className="text-gold underline underline-offset-4" data-testid="link-signup">Create an account</Link></p>
      </form>
    </AuthShell>
  );
}

export function Signup() {
  const { register, user } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", marketing_consent: true });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => { if (user) navigate("/portal", { replace: true }); }, [user, navigate]);

  const onSubmit = async (e) => {
    e.preventDefault(); setError(""); setLoading(true);
    try {
      await register(form);
      toast.success("Welcome — your portal is ready");
      navigate("/portal", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <AuthShell eyebrow="Create your account" title="Start your design portal" subtitle="Save every color, quiz result and estimate to organized properties (My Homes).">
      <form onSubmit={onSubmit} className="space-y-5" data-testid="signup-form">
        <GoogleButton label="Sign up with Google" />
        <Divider label="or with email" />
        <Field icon={<UserIcon size={16}/>} label="Full name">
          <input value={form.name} onChange={(e)=>setForm({...form,name:e.target.value})} className="input-plain" data-testid="signup-name" />
        </Field>
        <Field icon={<Mail size={16}/>} label="Email">
          <input required type="email" value={form.email} onChange={(e)=>setForm({...form,email:e.target.value})} className="input-plain" data-testid="signup-email" />
        </Field>
        <Field icon={<Lock size={16}/>} label="Password (8+ characters)">
          <input required minLength={8} type="password" value={form.password} onChange={(e)=>setForm({...form,password:e.target.value})} className="input-plain" data-testid="signup-password" />
        </Field>
        <label className="flex items-start gap-2 text-xs text-ink/70">
          <input type="checkbox" checked={form.marketing_consent} onChange={(e)=>setForm({...form,marketing_consent:e.target.checked})} className="mt-1" data-testid="signup-marketing" />
          <span>Send me color inspiration, guides and Beverly Hills Paint Co. updates. Unsubscribe anytime.</span>
        </label>
        {error && <p className="text-sm text-red-600" data-testid="signup-error">{error}</p>}
        <Button type="submit" disabled={loading} data-testid="signup-submit" className="w-full bg-gold text-white rounded-none hover:bg-gold-dark py-6 overline">
          {loading ? "Creating…" : (<span className="flex items-center justify-center gap-2"><UserPlus size={16}/> Create account</span>)}
        </Button>
        <p className="text-sm text-ink/70 text-center">Already have an account? <Link to="/login" className="text-gold underline underline-offset-4" data-testid="link-login">Sign in</Link></p>
      </form>
    </AuthShell>
  );
}

export function ForgotPassword() {
  const { forgotPassword } = useAuth();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const onSubmit = async (e) => {
    e.preventDefault(); setLoading(true);
    try { await forgotPassword(email); setSent(true); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setLoading(false); }
  };
  return (
    <AuthShell eyebrow="Forgot password" title="Reset your password" subtitle="Enter the email tied to your account and we'll send you a secure reset link.">
      {sent ? (
        <div data-testid="forgot-sent">
          <p className="text-ink/80">If an account exists for <b>{email}</b>, we've sent a reset link. Check your inbox (and spam folder).</p>
          <Link to="/login" className="mt-6 inline-block text-gold underline underline-offset-4">Back to sign in</Link>
        </div>
      ) : (
        <form onSubmit={onSubmit} className="space-y-5" data-testid="forgot-form">
          <Field icon={<Mail size={16}/>} label="Email">
            <input required type="email" value={email} onChange={(e)=>setEmail(e.target.value)} className="input-plain" data-testid="forgot-email" />
          </Field>
          <Button type="submit" disabled={loading} data-testid="forgot-submit" className="w-full bg-ink text-white rounded-none hover:bg-gold py-6 overline">{loading ? "Sending…" : "Send reset link"}</Button>
        </form>
      )}
    </AuthShell>
  );
}

export function ResetPassword() {
  const { resetPassword } = useAuth();
  const navigate = useNavigate();
  const [token, setToken] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    const u = new URL(window.location.href);
    setToken(u.searchParams.get("token") || "");
  }, []);
  const onSubmit = async (e) => {
    e.preventDefault(); setError(""); setLoading(true);
    try {
      await resetPassword(token, password);
      toast.success("Password updated. Please sign in.");
      navigate("/login", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };
  return (
    <AuthShell eyebrow="Reset password" title="Choose a new password" subtitle="Use at least 8 characters.">
      <form onSubmit={onSubmit} className="space-y-5" data-testid="reset-form">
        <Field icon={<Lock size={16}/>} label="New password">
          <input required minLength={8} type="password" value={password} onChange={(e)=>setPassword(e.target.value)} className="input-plain" data-testid="reset-password" />
        </Field>
        {error && <p className="text-sm text-red-600" data-testid="reset-error">{error}</p>}
        <Button type="submit" disabled={loading || !token} data-testid="reset-submit" className="w-full bg-ink text-white rounded-none hover:bg-gold py-6 overline">{loading ? "Updating…" : "Update password"}</Button>
        {!token && <p className="text-xs text-red-600">Missing or invalid token. Please use the link from your email.</p>}
      </form>
    </AuthShell>
  );
}

export function VerifyEmail() {
  const { confirmEmail } = useAuth();
  const [status, setStatus] = useState("verifying"); // verifying | ok | error
  const ran = useRef(false);
  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    const u = new URL(window.location.href);
    const t = u.searchParams.get("token");
    if (!t) { setStatus("error"); return; }
    confirmEmail(t).then(() => setStatus("ok")).catch(() => setStatus("error"));
  }, [confirmEmail]);
  return (
    <AuthShell eyebrow="Email verification" title={status === "ok" ? "You're verified" : status === "verifying" ? "Verifying…" : "Verification failed"}>
      {status === "verifying" && <Loader2 className="animate-spin text-gold" />}
      {status === "ok" && <div data-testid="verify-ok"><p className="text-ink/80">Your email is confirmed. You now have full access to ordering, estimates and downloads.</p><Link to="/portal" className="text-gold underline underline-offset-4 mt-6 inline-block">Go to my portal</Link></div>}
      {status === "error" && <div data-testid="verify-error"><p className="text-red-600">This verification link is invalid or has expired.</p><Link to="/portal" className="text-gold underline underline-offset-4 mt-6 inline-block">Back to portal</Link></div>}
    </AuthShell>
  );
}

// ---------------------------------------------------------------------------
// Small primitives
// ---------------------------------------------------------------------------
function Field({ icon, label, children, trailing }) {
  return (
    <label className="block">
      <div className="flex items-center justify-between mb-1">
        <span className="overline text-[10px] text-ink/60 flex items-center gap-2">{icon}{label}</span>
        {trailing}
      </div>
      <div className="border border-black/15 focus-within:border-gold px-3">{children}</div>
    </label>
  );
}
function Divider({ label }) {
  return (
    <div className="flex items-center gap-3 text-[10px] uppercase tracking-widest text-ink/40">
      <div className="h-px bg-black/10 flex-1" /><span>{label}</span><div className="h-px bg-black/10 flex-1" />
    </div>
  );
}
